import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@material-ui/core';
import { PlannerSideNav, TEXT, SPACE, pageSx, useSnackbar } from '../../shared_components';
import DashboardAnalytics from '../components/DashboardAnalytics';
import { getDashboardDataApi } from '../apiService';
import { checkApiStatus } from '../../../../utils_v2';
import { globalGetService } from '../../../../utils_v2/globalApiServices';
import { trackActivity } from '../../../../utils/mixpanel';
import CardComp from '../components/CardComp';
import PieChartComp from '../components/PieChartComp';
import GraphCard from '../components/GraphCard';
import DateRangeFilter from '../components/DateRangeFilter';
import DashboardKpiRow, { KPI_ICONS } from '../components/DashboardKpiRow';
import MetricCard from '../components/MetricCard';
import ScheduleOutlinedIcon from '@material-ui/icons/ScheduleOutlined';
import AttachMoneyOutlinedIcon from '@material-ui/icons/AttachMoneyOutlined';
import NotificationsActiveOutlinedIcon from '@material-ui/icons/NotificationsActiveOutlined';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import '../../shared_components/assets/styles/plannerTracker.scss';

const STATUS = { good: '#0ca30c', warning: '#c98a12', critical: '#d03b3b' };
const formatDays = (value) => (value === null || value === undefined ? value : Number(value).toFixed(1));

export const buildKpiDashboardCards=(data)=>([
    { key: 'plans_created', label: 'Plans Created', value: data?.plans_created?.count ?? 0, sub: data?.plans_created?.sub_label || 'Total plans created', icon: KPI_ICONS.plans_created, kpiColor: 'blue' },
    { key: 'projects_closed', label: 'Projects Closed', value: data?.projects_closed?.count ?? 0, sub: data?.projects_closed?.sub_label || 'Total projects closed', icon: KPI_ICONS.projects_closed, kpiColor: 'green' },
    { key: 'avg_project_duration_days', label: 'Avg Project Duration', value: data?.avg_project_duration_days?.value ?? 0, sub: data?.avg_project_duration_days?.sub_label || 'From launch to closure', icon: KPI_ICONS.avg_project_duration_days, kpiColor: 'orange' },
    { key: 'on_time_launch_rate_pct', label: 'On-Time Launch Rate', value: data?.on_time_launch_rate_pct?.value ?? 0, sub: data?.on_time_launch_rate_pct?.sub_label || 'Within planned start date', icon: KPI_ICONS.on_time_launch_rate_pct, kpiColor: 'purple' },
  ]);

export const buildDurationMetrics = (data) => ([
  { key: 'avg_planning_days', label: 'Avg. Planning Duration', value: data?.avg_planning_days, unit: 'd', format: formatDays },
  { key: 'avg_inspection_days', label: 'Avg. Inspection Duration', value: data?.avg_inspection_days, unit: 'd', format: formatDays },
  { key: 'avg_report_formation_days', label: 'Avg. Report Formation', value: data?.avg_report_formation_days, unit: 'd', format: formatDays },
  { key: 'avg_client_acceptance_days', label: 'Avg. Client Acceptance', value: data?.avg_client_acceptance_days, unit: 'd', format: formatDays },
  { key: 'avg_total_end_to_end_days', label: 'Avg. Total End-to-End', value: data?.avg_total_end_to_end_days, unit: 'd', format: formatDays, color: '#2878DF' },
]);

export const buildRevenueCycleMetrics = (data) => ([
  { key: 'avg_closed_to_invoice_days', label: 'Closed → Invoice Raised', value: data?.avg_closed_to_invoice_days, unit: 'd', format: formatDays },
  { key: 'avg_invoice_to_paid_days', label: 'Invoice Raised → Paid', value: data?.avg_invoice_to_paid_days, unit: 'd', format: formatDays },
  { key: 'invoice_pending', label: 'Invoice Pending', value: data?.invoice_pending, color: data?.invoice_pending > 0 ? STATUS.warning : undefined },
  { key: 'invoice_raised', label: 'Invoice Raised', value: data?.invoice_raised },
  { key: 'financially_closed', label: 'Financially Closed', value: data?.financially_closed, color: STATUS.good },
]);

export const buildInactivityMetrics = (data) => ([
  { key: 'alerts_sent', label: data?.alerts_sent?.label || '72h Alerts Sent', value: data?.alerts_sent?.count },
  { key: 'escalations', label: data?.escalations?.label || '96h Escalations', value: data?.escalations?.count, color: data?.escalations?.count > 0 ? STATUS.critical : undefined },
  { key: 'currently_active_alerts', label: 'Currently Active Alerts', value: data?.currently_active_alerts, color: data?.currently_active_alerts > 0 ? STATUS.critical : undefined },
  { key: 'overridden', label: 'Overridden', value: data?.overridden },
  { key: 'avg_response_time_hours', label: 'Avg. Response Time', value: data?.avg_response_time_hours, unit: 'h', format: formatDays },
  { key: 'resolved_under_24h', label: 'Resolved Under 24h', value: data?.resolved_under_24h, color: STATUS.good },
]);

export const buildClientEscalationMetrics = (data) => ([
  { key: 'total_raised', label: 'Total Raised', value: data?.total_raised, color: data?.total_raised > 0 ? STATUS.critical : undefined },
  ...((data?.by_lessor || []).map((row, index) => ({ key: `by_lessor_${index}`, label: row?.label, value: row?.count }))),
  { key: 'avg_resolution_hours', label: 'Avg. Resolution Time', value: data?.avg_resolution_hours, unit: 'h', format: formatDays },
  { key: 'avg_resolution_days', label: 'Avg. Resolution', value: data?.avg_resolution_days, unit: 'd', format: formatDays },
]);

const Dashboard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const [kpiCardData, setKpiCardData] = useState(null);
  const [dashboardData, setDashBoardData] = useState(null);

  useEffect(() => {
    getDashboardData();
    trackActivity('Project Management', {
      event_type: 'Page Visited',
      page_title: 'Planner Tracker Dashboard',
    });
  }, [])
  const getDashboardData = (overrides = {}) => {
    setLoading(true);
    globalGetService(getDashboardDataApi(), overrides).then((response) => {
      setLoading(false);
      if (checkApiStatus(response)) {
        const data = response?.data?.data;
        setDashBoardData(data);
        setKpiCardData(data?.kpis);
      } else {
        enqueueSnackbar('Error Occurred', { variant: 'error' });
      }
    });
  };
  return (
    <div style={{ display: 'flex', marginLeft: 56, marginTop: 15 }}>
      <PlannerSideNav activeKey="dashboard" />
      <section className="projects-management-apps" style={{ ...pageSx, flex: 1, minWidth: 0 }}>
        <div className='pt-scroll-panel--dashboard' style={{overflowY:'auto'}}>
          <Grid container spacing={1}>
            <Grid item md={12} xs={12}>
              <DateRangeFilter onApply={({ startDate, endDate } = {}) => {
                const query = {
                  ...(startDate ? { start_date: startDate } : {}),
                  ...(endDate ? { end_date: endDate } : {}),
                };
                getDashboardData(query);
                trackActivity('Project Management', {
                  event_type: 'Dashboard Date Filter Applied',
                  page_title: 'Planner Tracker Dashboard',
                  filter: query,
                });
              }} />
            </Grid>
            <Grid item md={12} xs={12}>
              <DashboardKpiRow cards={buildKpiDashboardCards(kpiCardData)} loading={isLoading} />
            </Grid>
            <Grid item md={6} xs={12}>
              <CardComp cardName='Projects By Lessor' cardData={dashboardData?.projects_by_lessor || null} loading={isLoading} />
            </Grid>
            <Grid item md={6} xs={12}>
              <CardComp cardName='Projects By Project Type' cardData={dashboardData?.projects_by_project_type || null} loading={isLoading} />
            </Grid>
            <Grid item md={8} xs={12} style={{ display: 'flex' }}>
              <GraphCard cardName="Assets By Location" cardData={dashboardData?.asset_portfolio?.by_location || null} loading={isLoading} />
            </Grid>
            <Grid item md={4} xs={12} style={{ display: 'flex' }}>
              <PieChartComp
                cardName="Assets By Type"
                cardData={dashboardData?.asset_portfolio?.by_asset_type || null}
                totalAssets={dashboardData?.asset_portfolio?.total_assets}
                loading={isLoading}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <CardComp cardName='Projects By Aircraft Type' cardData={dashboardData?.projects_by_aircraft_type || null} loading={isLoading} />
            </Grid>
            <Grid item md={6} xs={12}>
              <CardComp cardName='Projects By Engine Type' cardData={dashboardData?.projects_by_engine_type || null} loading={isLoading} />
            </Grid>
            <Grid item md={3} xs={12}>
              <MetricCard
                title="Duration Metrics"
                icon={<ScheduleOutlinedIcon />}
                iconBackground="#EEF4FF"
                iconColor="#2878DF"
                metrics={buildDurationMetrics(dashboardData?.duration_metrics)}
                loading={isLoading}
              />
            </Grid>
            <Grid item md={3} xs={12}>
              <MetricCard
                title="Revenue Cycle"
                icon={<AttachMoneyOutlinedIcon />}
                iconBackground="#EAFAF1"
                iconColor="#0ca30c"
                metrics={buildRevenueCycleMetrics(dashboardData?.revenue_cycle)}
                loading={isLoading}
              />
            </Grid>
            <Grid item md={3} xs={12}>
              <MetricCard
                title="Inactivity Stats"
                icon={<NotificationsActiveOutlinedIcon />}
                iconBackground="#FFF6E5"
                iconColor="#c98a12"
                metrics={buildInactivityMetrics(dashboardData?.inactivity_stats)}
                loading={isLoading}
              />
            </Grid>
            <Grid item md={3} xs={12}>
              <MetricCard
                title="Client Escalations"
                icon={<ReportProblemOutlinedIcon />}
                iconBackground="#FDECEC"
                iconColor="#d03b3b"
                metrics={buildClientEscalationMetrics(dashboardData?.client_escalations)}
                loading={isLoading}
              />
            </Grid>
          </Grid>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
